import { LinearProgress } from "@mui/material";
import { Box } from "@mui/system";
import { createHashRouter, Outlet, useNavigation } from "react-router-dom";
import { Contexts } from "../Contexts";
import { ArticlePage } from "../features/Articles/ArticlePage";
import { AssessmentPage } from "../features/Assessment";
import {
  AuthRedirect,
  ForbidAuth,
  RequireAuth,
  SignInPage,
} from "../features/Authorization";
import { CheckEmailPage } from "../features/Authorization/CheckEmailPage";
import { ForgotPasswordPage } from "../features/Authorization/ForgotPasswordPage";
import { PasswordChangedPage } from "../features/Authorization/PasswordChangedPage";
import { SetPasswordPage } from "../features/Authorization/SetPasswordPage";
import { SignOutPage } from "../features/Authorization/SignOutPage";
import { ClientsPage } from "../features/Clients/ClientsPage";
import { CoachesPage } from "../features/Coaches/Coaches.page";
import { SyncLocalFavorites } from "../features/Coaches/Favorites/useFavorites";
import { DashboardPage } from "../features/Dashboard";
import ErrorPage from "../features/ErrorHandling/ErrorPage";
import { CreateFeedbackPage } from "../features/Feedback/CreateFeedback.page";
import { ExternalFeedbackPage } from "../features/Feedback/ExternalFeedbackPage";
import { FeedbackResultsPage } from "../features/Feedback/FeedbackResults.page";
import { GetFeedbackPage } from "../features/Feedback/GetFeedback.page";
import { HelpPage } from "../features/Help/Help.page";
import { IntegrationResultPage } from "../features/Integrations/IntegrationResultPage";
import { MessagesPage } from "../features/Messages/Messages.page";
import { MyCalendarPage } from "../features/MyCalendar/MyCalendar.page";
import { MyTeamPage } from "../features/MyTeam/MyTeam";
import { NotesPage } from "../features/Notes/NotesPage";
import { ReportsPage } from "../features/Reports/ReportsPage";
import { SessionsPage } from "../features/Sessions";
import { SessionPage } from "../features/Sessions/Session";
import { StartSessionPage } from "../features/Sessions/StartSession.page";
import { SettingsPage } from "../features/Settings/Settings.page";
import { UserProfilePage } from "../features/Settings/UserProfilePage";
import { StrengthsPage } from "../features/Strengths";
import { TeamPage } from "../features/Team/Team.page";
import { MyValuesPage, SetValuesPage } from "../features/Values";
import { rolesDefByRoute, routes } from "./constants";

const Root = (props) => {
  const navigation = useNavigation();

  // Uncomment to test ErrorPage
  // console.log(navigation.some.prop);

  return (
    <Contexts>
      {/* TODO: remove SyncLocalFavorites */}
      <SyncLocalFavorites />
      {navigation.state === "loading" && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      <Outlet />
    </Contexts>
  );
};

export const router = createHashRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <AuthRedirect /> },
      {
        path: routes.signIn,
        element: (
          <ForbidAuth>
            <SignInPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.signOut,
        element: <SignOutPage />,
      },
      {
        path: routes.setPassword,
        element: (
          <ForbidAuth>
            <SetPasswordPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.setPassword_TODO_1_JAKUB_K,
        element: (
          <ForbidAuth>
            <SetPasswordPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.resetPassword,
        element: (
          <ForbidAuth>
            <SetPasswordPage isExistingUser />
          </ForbidAuth>
        ),
      },
      {
        path: routes.resetPassword_TODO_1_JAKUB_K,
        element: (
          <ForbidAuth>
            <SetPasswordPage isExistingUser />
          </ForbidAuth>
        ),
      },
      {
        path: routes.passwordChanged,
        element: (
          <ForbidAuth>
            <PasswordChangedPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.forgotPassword,
        element: (
          <ForbidAuth>
            <ForgotPasswordPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.checkEmail,
        element: (
          <ForbidAuth>
            <CheckEmailPage />
          </ForbidAuth>
        ),
      },
      {
        path: routes.dashboard,
        element: (
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.assessment,
        element: (
          <RequireAuth>
            <AssessmentPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.strengths,
        element: (
          <RequireAuth>
            <StrengthsPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.setValues,
        element: (
          <RequireAuth>
            <SetValuesPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.myValues,
        element: (
          <RequireAuth>
            <MyValuesPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.sessions,
        children: [
          {
            index: true,
            element: (
              <RequireAuth>
                <SessionsPage />
              </RequireAuth>
            ),
          },
          {
            path: routes.session,
            element: (
              <RequireAuth>
                <SessionPage />
              </RequireAuth>
            ),
          },
          {
            path: routes.startSession,
            element: (
              <RequireAuth>
                <StartSessionPage />
              </RequireAuth>
            ),
          },
        ],
      },
      {
        path: routes.coaches,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.coaches]}>
            <CoachesPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.myCalendar,
        element: (
          <RequireAuth>
            <MyCalendarPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.settings,
        element: (
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.profile,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.profile]}>
            <UserProfilePage />
          </RequireAuth>
        ),
      },
      {
        path: routes.syncSuccess,
        element: (
          <RequireAuth>
            <IntegrationResultPage result="success" />
          </RequireAuth>
        ),
      },
      {
        path: routes.syncError,
        element: (
          <RequireAuth>
            <IntegrationResultPage result="error" />
          </RequireAuth>
        ),
      },
      {
        path: routes.help,
        element: (
          <RequireAuth>
            <HelpPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.getFeedback,
        element: (
          <RequireAuth>
            <GetFeedbackPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.createFeedbackForm,
        element: (
          <RequireAuth>
            <CreateFeedbackPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.copyFeedbackForm,
        element: (
          <RequireAuth>
            <CreateFeedbackPage isCopy />
          </RequireAuth>
        ),
      },
      {
        path: routes.editFeedbackForm,
        element: (
          <RequireAuth>
            <CreateFeedbackPage isEdit />
          </RequireAuth>
        ),
      },
      {
        path: routes.feedbackResults,
        element: (
          <RequireAuth>
            <FeedbackResultsPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.externalFeedbackPage,
        element: <ExternalFeedbackPage />,
      },
      {
        path: routes.team,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.team]}>
            <TeamPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.myTeam,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.myTeam]}>
            <MyTeamPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.clients,
        element: (
          <RequireAuth rolesDef={rolesDefByRoute[routes.clients]}>
            <ClientsPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.messages,
        element: (
          <RequireAuth>
            <MessagesPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.notes,
        element: (
          <RequireAuth>
            <NotesPage />
          </RequireAuth>
        ),
      },
      {
        path: routes.reports,
        element: (
          <RequireAuth>
            <ReportsPage rolesDef={rolesDefByRoute[routes.reports]} />
          </RequireAuth>
        ),
      },
      {
        path: routes.article,
        element: (
          <RequireAuth>
            <ArticlePage />
          </RequireAuth>
        ),
      },
    ],
  },
]);
